import { addCalendarDays } from "@/lib/timezone";

export interface OverrideRow {
  date: string;
  type: "add" | "block";
  startTime?: string | null;
  endTime?: string | null;
}

/**
 * One line of a therapist's calendar exceptions as an admin reads them.
 * `off` is a run of consecutive full-day blocks (a vacation, sick leave), so a
 * two-week break reads as one line instead of fourteen. `block` (part of a day
 * blocked) and `add` (extra hours on a day) always cover a single date.
 */
export interface OverridePeriod {
  kind: "off" | "block" | "add";
  startDate: string;
  endDate: string;
  days: number;
  startTime: string | null;
  endTime: string | null;
}

// Same rule as applyOverrides in src/lib/slots.ts: a block without both bounds
// (no start, or a legacy row missing its end) takes out the whole day.
function isFullDayBlock(row: OverrideRow): boolean {
  return row.type === "block" && (!row.startTime || !row.endTime);
}

function sortRows(rows: OverrideRow[]): OverrideRow[] {
  return [...rows].sort(
    (a, b) => a.date.localeCompare(b.date) || (a.startTime ?? "").localeCompare(b.startTime ?? ""),
  );
}

export function groupOverridePeriods(rows: OverrideRow[]): OverridePeriod[] {
  const sorted = sortRows(rows);
  const periods: OverridePeriod[] = [];
  let openOff: OverridePeriod | null = null;

  for (const row of sorted) {
    if (isFullDayBlock(row)) {
      if (openOff && row.date === openOff.endDate) continue;
      if (openOff && row.date === addCalendarDays(openOff.endDate, 1)) {
        openOff.endDate = row.date;
        openOff.days += 1;
        continue;
      }
      openOff = { kind: "off", startDate: row.date, endDate: row.date, days: 1, startTime: null, endTime: null };
      periods.push(openOff);
      continue;
    }
    periods.push({
      kind: row.type,
      startDate: row.date,
      endDate: row.date,
      days: 1,
      startTime: row.startTime ?? null,
      endTime: row.endTime ?? null,
    });
  }

  return periods.sort((a, b) => a.startDate.localeCompare(b.startDate));
}

export interface DayOverrideMarker {
  kind: OverridePeriod["kind"];
  label: string;
}

/**
 * Per-date labels for a calendar cell, keyed by YYYY-MM-DD. A full-day block
 * hides the rest of that date's rows because nothing else on it is bookable.
 */
export function dayOverrideMarkers(rows: OverrideRow[]): Record<string, DayOverrideMarker[]> {
  const byDate: Record<string, DayOverrideMarker[]> = {};
  const offDates = new Set(rows.filter(isFullDayBlock).map((r) => r.date));

  for (const row of sortRows(rows)) {
    if (offDates.has(row.date)) {
      byDate[row.date] = [{ kind: "off", label: "Ne radi" }];
      continue;
    }
    // applyOverrides ignores an add without both bounds, so it marks nothing.
    if (row.type === "add" && (!row.startTime || !row.endTime)) continue;
    const range = `${row.startTime}-${row.endTime}`;
    const marker: DayOverrideMarker =
      row.type === "block"
        ? { kind: "block", label: `Blokirano ${range}` }
        : { kind: "add", label: `Dodatno ${range}` };
    (byDate[row.date] ??= []).push(marker);
  }
  return byDate;
}
